/**
 * @fileoverview Componente Organismo para la gestión y visualización de Respuestas Rápidas agrupadas por equipo y tipo, con filtros y búsqueda avanzada.
 * Cumple con Atomic Design (Organism) y optimizaciones de renderizado (React.memo, useCallback, useMemo).
 *
 * @module FastAnswersMC
 * @category Component - Organism
 * @since 2.0.0
 * @version 3.1.0
 */

import React, { memo, useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import useFastAnswers from "./hooks/useFastAnswers";
import FastAnswerTypeSelectMC from "./FastAnswerTypeSelectMC";
import TeamSelect from "../../components/TeamSelect";
import FastAnswerTeamGroupMC from "./FastAnswerTeamGroupMC";
import SearchMC from "../../components/SearchMC";
import { sharingInformationService } from "../../../services/sharingInformationService";

/**
 * Componente Organismo para mostrar y filtrar Respuestas Rápidas agrupadas por equipo y tipo.
 */
const FastAnswersMC = memo(({
  currentCustomer,
  platform,
  closeModal
}) => {
  const { t } = useTranslation('FastAnswer');
  const [search, setSearch] = useState('');
  const [answerType, setAnswerType] = useState('all');
  const [teamId, setTeamId] = useState('all');
  const [appliedFilters, setAppliedFilters] = useState(false);

  /**
   * Parámetros para el hook de carga de respuestas rápidas.
   * @type {Object}
   */
  const hookParams = useMemo(() => ({
    searchTerm: search,
    type: answerType,
    customer: currentCustomer?.id,
    team: teamId,
    platform
  }), [search, answerType, currentCustomer?.id, teamId, platform]);

  /**
   * Datos de equipos y respuestas rápidas agrupadas, obtenidos del hook useFastAnswers.
   */
  const { teams, groupedFastAnswers } = useFastAnswers(hookParams);

  /**
   * Indica si hay filtros aplicados (equipo, tipo o búsqueda).
   */
  const hasFiltersApplied = useMemo(() => {
    return teamId !== 'all' || answerType !== 'all' || !!search;
  }, [teamId, answerType, search]);

  /**
   * Efecto para actualizar el estado de filtros aplicados.
   */
  useEffect(() => {
    setAppliedFilters(hasFiltersApplied);
  }, [hasFiltersApplied]);

  /**
   * Maneja la selección de una respuesta rápida y cierra el sidebar.
   */
  const handleSelectAnswer = useCallback((answer, actionType) => {
    sharingInformationService.setSubject({
      answer,
      actionType,
      onSelectAnswer: true,
    });
    sharingInformationService.setSubject({});
  }, []);

  /**
   * Maneja el cambio en el campo de búsqueda.
   */
  const handleSearchChange = useCallback((searchValue) => {
    setSearch(searchValue);
  }, []);

  /**
   * Maneja la selección de equipo.
   */
  const handleTeamSelect = useCallback((selectedTeamId) => {
    setTeamId(selectedTeamId);
  }, []);

  /**
   * Maneja la selección de tipo de respuesta.
   */
  const handleAnswerTypeSelect = useCallback((value) => {
    setAnswerType(value);
  }, []);

  /**
   * Placeholder traducido para el campo de búsqueda.
   */
  const searchPlaceholder = useMemo(() => t('searchPlaceholder'), [t]);
  /**
   * Placeholder traducido para el selector de equipo.
   */
  const selectTeamPlaceholder = useMemo(() => t('selectTeam'), [t]);

  return (
    <div>
      <div>
        <SearchMC
          placeholder={searchPlaceholder}
          onChange={handleSearchChange}
          inputClassName="tw-py-3"
          debounceTime={500}
        />
        <div className="p-relative mt-10">
          <TeamSelect
            defaultValue={teamId}
            onSelect={handleTeamSelect}
            placeholder={selectTeamPlaceholder}
            showPersonal
            useFloatingLabel
            teamOptions={teams}
          />
        </div>
        <FastAnswerTypeSelectMC
          answerType={answerType}
          handleSelect={handleAnswerTypeSelect}
        />
      </div>

      <FastAnswerTeamGroupMC
        teams={groupedFastAnswers}
        fullButtons
        appliedFilters={appliedFilters}
        onSelectAnswer={handleSelectAnswer}
      />
    </div>
  );
});

FastAnswersMC.displayName = "FastAnswersMC";

export default FastAnswersMC;
